/* @jsx h */
import { defineComponent, h, ref, watch } from 'vue'
import type { FormOptions, Path, SchemaFormInstance, ValidationResult } from './types'
import { normalizeSchema, validateValue } from './schema'
import { defaultTemplates, type Templates } from './templates'
import { defaultI18n } from './i18n'
import { FieldRenderer } from './FieldRenderer'

export interface SchemaFormProps {
  /** TypeBox or Zod schema */
  schema: any
  /** Rendering options (mirrors tcomb-form's options prop) */
  options?: FormOptions
  /** Current form value (controlled) */
  value?: any
  /** Context passed through to error functions and templates */
  context?: any
}

/**
 * SchemaForm — auto-generates forms from TypeBox or Zod schemas.
 *
 * Mirrors tcomb-form's API:
 *   - Expose getValue() / validate() via template ref
 *   - options.fields for per-field config
 *   - options.template / options.factory for custom rendering
 *   - options.order for field order
 *   - options.auto for label/placeholder generation
 *
 * @example
 * const formRef = ref<SchemaFormInstance>()
 *
 * <SchemaForm
 *   ref="formRef"
 *   :schema="PersonSchema"
 *   :options="{ fields: { name: { label: 'Full Name' } } }"
 *   :value="formValue"
 *   @change="(v) => formValue = v"
 * />
 *
 * const value = formRef.value?.getValue() // null if invalid
 */
export const SchemaForm = defineComponent({
  name: 'SchemaForm',
  props: {
    schema: { required: true as const, type: Object as any },
    options: { type: Object as any },
    value: { type: null as any, default: undefined },
    context: { type: null as any, default: undefined },
  },
  // Typed emits so Vue overrides the HTML onChange event signature in JSX
  emits: {
    change: (_value: any, _path: Path) => true,
  },

  setup(props, { emit, expose }) {
    const localValue = ref<any>(props.value ?? undefined)
    const showErrors = ref(false)
    const lastValidation = ref<ValidationResult>({ isValid: true, errors: [] })

    watch(
      () => props.value,
      (v) => { localValue.value = v },
    )

    function runValidation(): ValidationResult {
      const normalized = normalizeSchema(props.schema)
      return validateValue(normalized, localValue.value)
    }

    const instance: SchemaFormInstance = {
      getValue(): any | null {
        const result = runValidation()
        lastValidation.value = result
        showErrors.value = true
        return result.isValid ? localValue.value : null
      },
      validate(): ValidationResult {
        const result = runValidation()
        lastValidation.value = result
        return result
      },
    }

    expose(instance)

    const handleChange = (value: any, path: Path) => {
      localValue.value = value
      emit('change', value, path)
    }

    return () => {
      const normalized = normalizeSchema(props.schema)
      const opts = props.options ?? {}

      return (
        <FieldRenderer
          schema={normalized}
          value={localValue.value}
          options={opts}
          path={[]}
          fieldName=""
          auto={opts.auto ?? 'labels'}
          disabled={!!opts.disabled}
          context={props.context ?? opts.context}
          showErrors={showErrors.value}
          validationResult={lastValidation.value}
          onChange={handleChange}
        />
      )
    }
  },
})

// ---------------------------------------------------------------------------
// Static properties (mirrors tcomb-form's Form.templates / Form.i18n)
// ---------------------------------------------------------------------------

;(SchemaForm as any).templates = defaultTemplates as Templates
;(SchemaForm as any).i18n = defaultI18n
